import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChartLine, ShieldStar, Clock, Warning } from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const MONTH_LABELS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function buildBuckets(nMonths) {
  const now = new Date();
  const buckets = [];
  for (let i = nMonths - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    buckets.push({
      key,
      label: `${MONTH_LABELS_IT[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`,
      plan: 0, remote: 0, uncovered: 0, total: 0, count: 0,
    });
  }
  return buckets;
}

/**
 * Props:
 * - logs: array of assistance-log objects (with minutes_from_plan / minutes_from_remote / minutes_uncovered / minutes_used / date)
 */
export default function MinutesUsageChart({ logs }) {
  const [range, setRange] = useState(6); // months window

  const data = useMemo(() => {
    const buckets = buildBuckets(range);
    const byKey = Object.fromEntries(buckets.map(b => [b.key, b]));
    for (const l of logs || []) {
      const d = (l.date || "").slice(0, 7); // YYYY-MM
      if (!d) continue;
      const b = byKey[d];
      if (!b) continue;
      b.plan += l.minutes_from_plan || 0;
      b.remote += l.minutes_from_remote || 0;
      b.uncovered += l.minutes_uncovered || 0;
      b.total += l.minutes_used || 0;
      b.count += 1;
    }
    return buckets;
  }, [logs, range]);

  const totals = useMemo(() => data.reduce((acc, b) => ({
    plan: acc.plan + b.plan,
    remote: acc.remote + b.remote,
    uncovered: acc.uncovered + b.uncovered,
    total: acc.total + b.total,
    count: acc.count + b.count,
  }), { plan: 0, remote: 0, uncovered: 0, total: 0, count: 0 }), [data]);

  const hasAny = totals.total > 0;

  return (
    <Card className="border-border/70 rounded-lg overflow-hidden" data-testid="minutes-usage-chart-card">
      <div className="px-4 py-3 border-b border-border/60 bg-secondary/40 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ChartLine size={18} weight="duotone" className="text-primary" />
          <h2 className="font-display font-bold tracking-tight">Consumo minuti · trend mensile</h2>
        </div>
        <div className="flex gap-1" data-testid="chart-range-buttons">
          {[3, 6, 12].map((n) => (
            <Button
              key={n}
              size="sm"
              variant={range === n ? "default" : "outline"}
              onClick={() => setRange(n)}
              className="rounded-full h-7 px-3 text-xs font-mono"
              data-testid={`chart-range-${n}`}
            >{n}m</Button>
          ))}
        </div>
      </div>

      <div className="p-4">
        {!hasAny ? (
          <div className="text-center text-sm text-muted-foreground/70 font-mono py-8">
            — Nessun consumo registrato negli ultimi {range} mesi —
          </div>
        ) : (
          <>
            <div className="h-[220px] w-full" data-testid="minutes-usage-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fontFamily: "monospace", fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fontFamily: "monospace", fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false} axisLine={{ stroke: "hsl(var(--border))" }}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 6,
                      fontFamily: "monospace",
                      fontSize: 12,
                      color: "hsl(var(--foreground))",
                    }}
                    labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
                    itemStyle={{ padding: 0 }}
                    formatter={(v, name) => [`${v} min`, name]}
                  />
                  <Legend
                    wrapperStyle={{ fontFamily: "monospace", fontSize: 11, paddingTop: 8 }}
                    iconType="square"
                  />
                  <Bar dataKey="plan" name="Piano gestito" stackId="a" fill="hsl(24 95% 53%)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="remote" name="Saldo remoto" stackId="a" fill="hsl(200 78% 55%)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="uncovered" name="Non coperti" stackId="a" fill="hsl(0 70% 55%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Legend/summary tiles */}
            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="rounded-md border border-border/60 bg-secondary/30 p-2.5" data-testid="chart-summary-plan">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <ShieldStar size={12} className="text-primary" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Piano</span>
                </div>
                <div className="font-mono text-sm font-bold">{totals.plan} min</div>
              </div>
              <div className="rounded-md border border-border/60 bg-secondary/30 p-2.5" data-testid="chart-summary-remote">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Clock size={12} style={{ color: "hsl(200 78% 55%)" }} />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Remoto</span>
                </div>
                <div className="font-mono text-sm font-bold">{totals.remote} min</div>
              </div>
              <div className={`rounded-md border p-2.5 ${totals.uncovered > 0 ? "border-rose-500/40 bg-rose-500/5" : "border-border/60 bg-secondary/30"}`} data-testid="chart-summary-uncovered">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Warning size={12} className={totals.uncovered > 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground"} />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Scoperti</span>
                </div>
                <div className={`font-mono text-sm font-bold ${totals.uncovered > 0 ? "text-rose-700 dark:text-rose-400" : ""}`}>{totals.uncovered} min</div>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-muted-foreground">
              <span>Totale periodo: <span className="text-foreground font-semibold">{totals.total} min</span></span>
              <span>{totals.count} intervent{totals.count === 1 ? "o" : "i"}</span>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
